<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：MFA認証スイッチ変更
 *
 * 作成者：2022/05/27 ノア）馬
 * 最終修正者：2022/05/27 ノア）馬
 *
 -->
<template>
  <div>
    <!-- MFA認証スイッチ変更 -->
    <ttiDialog
      :isShow="isShow"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('mfaSwitch.title')"
      width="400px"
    >
      <template slot="body">
        <el-form
          :model="mfaSwitchForm"
          ref="mfaVerifFormRef"
          class="mfa-switch-form"
          label-position="top"
          label-width="80px"
        >
          <el-form-item prop="mfaFlag" :label="$t('mfaSwitch.mfaDescribes')">
            <el-radio-group v-model="mfaSwitchForm.mfaFlag">
              <el-radio :label="mfaFlagConstant.MFA_VALID_FLAG">{{ $t('mfaSwitch.mfaFlagLabel') }}</el-radio>
              <el-radio :label="mfaFlagConstant.MFA_INVALID_FLAG">{{ $t('mfaSwitch.mfaFlagOtherLabel') }}</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
      </template>
      <template slot="foot">
        <div class="mfa-switch-foot">
          <div class="tti-bottom-left-area">
            <el-button
              class="tti-btn-default tti-btn-001"
              @click="onCancelButtonClick"
              v-tooltip="{
                content: `${$t('mfaSwitch.mfaCancel')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              <tti-svg-icon icon-name="su_005"></tti-svg-icon>
              {{ $t('buttons.cancel') }}
            </el-button>
          </div>
          <div class="tti-bottom-right-area">
            <el-button
              class="tti-btn-default tti-btn-001"
              @click="onSaveButtonClick"
              v-tooltip="{
                content: `${$t('mfaSwitch.mfaSave')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              <tti-svg-icon icon-name="su_002"></tti-svg-icon>
              {{ $t('buttons.save') }}
            </el-button>
          </div>
        </div>
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { i18n } from '@/locales'
import { isEmpty } from 'lodash'

import { MFASwitchParam, MFAInfo } from '@/models'
import { dialog } from '@/store/modules'
import { postMFASwitch, getMFAInfo } from '@/store/apis'
import { msgBoxService } from '@/commons/services/msgBoxService'
import TtiDialog from '@/components/TtiDialog.vue'
import { MFAFlag } from '@/commons/constants'
import manualConfig from '@/config/manualConfig'

@Component({
  name: 'mfaSet',
  components: { TtiDialog }
})
export default class MfaSet extends Vue {
  /**
   * MFA検証フラグ
   */
  mfaFlagConstant = MFAFlag

  /**
   * MFA
   */
  mfaSetListCache: MFAInfo = {
    mfaFlag: ''
  }

  /**
   * MFA認証スイッチフォームのref
   */
  @Ref() readonly mfaVerifFormRef!: Form

  mfaInfo: MFAInfo = {
    mfaFlag: ''
  }

  /**
   * ダイアログボックスを表示するかどうかを取得する（画面用）
   *
   * @returns {boolean} ダイアログボックスを表示するかどうか（画面用）
   */
  get isShow(): boolean | undefined {
    if (dialog.isMFASwitchDialogShow) {
      this.fetchMFAInfoData()
    }
    return dialog.isMFASwitchDialogShow
  }

  /**
   * ダイアログボックスを表示するかどうかを設定する（画面用）
   */
  set isShow(value: boolean | undefined) {
    this.handelClose()
  }

  /**
   *  MFA情報を取得する処理
   *
   * @returns {void}
   */
  async fetchMFAInfoData(): Promise<void> {
    try {
      this.mfaInfo = await getMFAInfo()

      if (!isEmpty(this.mfaInfo)) {
        this.mfaSwitchForm.mfaFlag = this.mfaInfo.mfaFlag
        this.mfaSetListCache.mfaFlag = this.mfaSwitchForm.mfaFlag
      }
    } catch (e) {
      this.$log.debug('MfaSwitch > fetchMFAInfoData: e=', e)
      throw e
    }
  }

  /**
   * 表示状態変更
   *
   * @returns {void}
   */
  onHandelDisplay(): void {
    this.handelClose()
  }

  /**
   * MFA認証設定サブ画面のマニュアルキーを取得
   *
   * @returns {string} MFA認証設定サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.mfaSet
  }

  /**
   * MFA認証スイッチフラグ
   */
  mfaSwitchForm: MFASwitchParam = {
    mfaFlag: MFAFlag.MFA_INVALID_FLAG
  }

  /**
   * MFA認証スイッチ確認ボタンをクリックする
   *
   * @returns {void}
   */
  onSaveButtonClick(): void {
    if (!this.checkDataChanged()) {
      msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
      return
    }

    const params = {
      mfaFlag: this.mfaSwitchForm.mfaFlag
    }

    this.doMFASwitch(params)
  }

  /**
   * リストが変更されましたかをチェックする。
   *
   * @returns {boolean} isChanged
   */
  checkDataChanged(): boolean {
    return this.mfaSetListCache.mfaFlag !== this.mfaSwitchForm.mfaFlag
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.mfaSwitchForm.mfaFlag = this.mfaSetListCache.mfaFlag
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 操作ポップアップボックスが表示されている、または表示されていない
   *
   * @returns {void}
   */
  handelClose(): void {
    dialog.setMFASwitchIsShow()
    this.mfaVerifFormRef.clearValidate()
  }
  /**
   * MFA認証スイッチを変更する
   *
   * @param {MFASwitchParam} mfaSwitchParam MFA認証スイッチのパラメータ
   * @returns {void}
   */
  async doMFASwitch(mfaSwitchParam: MFASwitchParam): Promise<void> {
    try {
      await postMFASwitch(mfaSwitchParam)

      this.doWhenUpdateSuccess()
    } catch (e) {
      Vue.$log.debug('MfaSet > doMFASwitch: e=', e)
      throw e
    }
  }

  /**
   * パスワードを変更した時の処理
   *
   * @returns {void}
   */
  doWhenUpdateSuccess(): void {
    const confirmCallback = () => {
      this.handelClose()
    }

    msgBoxService.info(i18n.t('mfaSwitch.dataSaved').toString(), confirmCallback)
  }
}
</script>

<style scoped lang="scss">
.mfa-switch-form {
  display: flex;
  justify-content: center;
}

.mfa-switch-foot {
  display: flex;
  justify-content: space-between;
}

.mfa-switch-right-area button {
  margin-right: 0;
}
</style>
